import { CSSResultGroup, html, LitElement, nothing, PropertyValues } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { classMap } from 'lit/directives/class-map.js'

import { style } from './password-checker.style.js'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import { t } from '@cloudsuite/mosaic-shared/translations/translator.js'
import { initializeIconsByRole } from '@cloudsuite/mosaic-shared/icons/library.js'


initializeIconsByRole({
  'csc-password-checker.met': { name: 'check', style: 'regular' },
  'csc-password-checker.unmet': { name: 'hyphen', style: 'regular' },
})

export interface PasswordCriteria {
  enabled: boolean
  min_length: number
  min_digits: number
  min_symbols: number
  min_uppercase: number
  min_lowercase: number
  messages?: Partial<Record<CriterionKey, string>>
}

export const criterionKeys = ['length', 'digit', 'symbol', 'lowercase', 'uppercase'] as const
export type CriterionKey = typeof criterionKeys[number]

export interface CriterionResult {
  key: CriterionKey
  min: number
  met: boolean
}

const CRITERIA_PATH = 'api/v1/password/criteria/'

export const PasswordCriteriaDefault: PasswordCriteria = {
  enabled: false,
  min_length: 0,
  min_digits: 0,
  min_symbols: 0,
  min_uppercase: 0,
  min_lowercase: 0,
}

const minimums: Record<CriterionKey, (criteria: PasswordCriteria) => number> = {
  length: (criteria) => criteria.min_length,
  digit: (criteria) => criteria.min_digits,
  symbol: (criteria) => criteria.min_symbols,
  lowercase: (criteria) => criteria.min_lowercase,
  uppercase: (criteria) => criteria.min_uppercase,
}

const counters: Record<CriterionKey, (password: string) => number> = {
  length: (password) => [...password].length,
  digit: (password) => password.match(/\p{Nd}/gu)?.length ?? 0,
  symbol: (password) => password.match(/[^\p{L}\p{N}\s]/gu)?.length ?? 0,
  lowercase: (password) => password.match(/\p{Ll}/gu)?.length ?? 0,
  uppercase: (password) => password.match(/\p{Lu}/gu)?.length ?? 0,
}

export const checkPassword = (password: string, criteria: PasswordCriteria): CriterionResult[] => {
  if (!criteria.enabled) return []

  return criterionKeys
    .map((key) => ({ key, min: minimums[key](criteria) ?? 0 }))
    .filter(({ min }) => min > 0)
    .map(({ key, min }) => ({ key, min, met: counters[key](password) >= min }))
}

export class PasswordCheckerChangeEvent extends Event {
  static type = 'csc-password-checker-change' as const
  valid: boolean

  constructor(valid: boolean) {
    super(PasswordCheckerChangeEvent.type, { bubbles: true, composed: true })
    this.valid = valid
  }
}

@customElement('csc-password-checker')
export class CSCPasswordChecker extends LitElement {
  static override styles: CSSResultGroup = [hostStyle, style]

  @property({
    type: Object,
    converter: {
      fromAttribute(value): PasswordCriteria {
        if (value && value.length > 0) {
          return { ...PasswordCriteriaDefault, ...JSON.parse(decodeURI(value)) }
        }

        return PasswordCriteriaDefault
      }
    }
  })
  criteria: PasswordCriteria = PasswordCriteriaDefault

  @property({ type: String, attribute: 'api-base-url' })
  apiBaseUrl = ''

  @state()
  private password = ''

  private lastValid: boolean | null = null

  get results(): CriterionResult[] {
    return checkPassword(this.password, this.criteria)
  }

  get valid(): boolean {
    return this.results.every(({ met }) => met)
  }

  protected override willUpdate(changedProperties: PropertyValues<this>): void {
    super.willUpdate(changedProperties)

    if (changedProperties.has('apiBaseUrl') && this.apiBaseUrl && !this.hasAttribute('criteria')) {
      this.fetchCriteria()
    }
  }

  protected override updated(changedProperties: PropertyValues<this>): void {
    super.updated(changedProperties)

    const valid = this.valid
    if (valid !== this.lastValid) {
      this.lastValid = valid
      this.dispatchEvent(new PasswordCheckerChangeEvent(valid))
    }
  }

  private get criteriaUrl(): URL {
    const base = this.apiBaseUrl.endsWith('/') ? this.apiBaseUrl : `${this.apiBaseUrl}/`
    return new URL(CRITERIA_PATH, new URL(base, window.location.href))
  }

  private async fetchCriteria() {
    try {
      const response = await fetch(this.criteriaUrl, {
        headers: { Accept: 'application/json' },
      })

      if (!response.ok) throw new Error(`Failed to fetch password criteria: ${response.status}`)

      this.criteria = { ...PasswordCriteriaDefault, ...await response.json() }
    } catch (error) {
      console.error(error)
    }
  }

  private readValue = (event: Event) => {
    const value = (event.target as { value?: unknown } | null)?.value
    if (typeof value === 'string') {
      this.password = value
    }
  }

  private onSlotChange = (event: Event) => {
    const slot = event.target as HTMLSlotElement
    const input = slot.assignedElements({ flatten: true })
      .find((element): element is Element & { value: string } => typeof (element as { value?: unknown }).value === 'string')

    this.password = input?.value ?? ''
  }

  private renderLabel(result: CriterionResult) {
    switch (result.key) {
      case 'length':
        return t('min_length', { ns: 'csc-password-checker', count: result.min, default: 'At least {{count}} character', default_plural: 'At least {{count}} characters' })
      case 'digit':
        return t('min_digits', { ns: 'csc-password-checker', count: result.min, default: 'At least {{count}} digit', default_plural: 'At least {{count}} digits' })
      case 'symbol':
        return t('min_symbols', { ns: 'csc-password-checker', count: result.min, default: 'At least {{count}} symbol', default_plural: 'At least {{count}} symbols' })
      case 'lowercase':
        return t('min_lowercase', { ns: 'csc-password-checker', count: result.min, default: 'At least {{count}} lowercase letter', default_plural: 'At least {{count}} lowercase letters' })
      case 'uppercase':
        return t('min_uppercase', { ns: 'csc-password-checker', count: result.min, default: 'At least {{count}} uppercase letter', default_plural: 'At least {{count}} uppercase letters' })
    }
  }

  private renderCriterion = (result: CriterionResult) => html`
    <cse-list-item class=${classMap({ criterion: true, met: result.met })} data-criterion=${result.key}>
      <cse-text variant="caption-m" has-icon>
        <cse-icon slot="prefix" icon-role=${result.met ? 'csc-password-checker.met' : 'csc-password-checker.unmet'}></cse-icon>
        ${this.renderLabel(result)}
        <span class="visually-hidden">
          ${result.met
            ? t('criterion_met', { ns: 'csc-password-checker', default: '(met)' })
            : t('criterion_not_met', { ns: 'csc-password-checker', default: '(not met)' })}
        </span>
      </cse-text>
    </cse-list-item>
  `

  override render() {
    const results = this.results

    return html`
      <div class="csc-password-checker" @input=${this.readValue} @change=${this.readValue}>
        <slot @slotchange=${this.onSlotChange}></slot>
        ${results.length > 0 ? html`
          <cse-list class="criteria">
            <cse-list-item>
              <cse-text variant="caption-m">
                ${t('title', { ns: 'csc-password-checker', default: 'Password must meet the following criteria:' })}
              </cse-text>
            </cse-list-item>
            ${results.map(this.renderCriterion)}
          </cse-list>
        ` : nothing}
      </div>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'csc-password-checker': CSCPasswordChecker
  }

  interface HTMLElementEventMap {
    [PasswordCheckerChangeEvent.type]: PasswordCheckerChangeEvent
  }
}
